import React, { useState } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { colors } from '../../../theme/palette'
import { Search as SearchIcon, StarFill, Close } from '../../../assets/icons'
import { Input } from '../FormElememts/stories/Input.stories'
import { Button } from '../Button/Button'
import { baseButton } from '../../../theme/reuseables'

type RuleNames =
    | 'label'
    | 'input'
    | 'container'
    | 'icon'
    | 'searchButton'
    | 'button'

interface StyleProps {
    query: string
    handleSearch?: () => void
    typeSearch?: string
}

type Props = {
    label: string
    required: boolean
    typeSearch: string
    searchButtonLabel: string
    style?: React.CSSProperties
    onSearch?: (search: string) => void
    onChange?: (search: string) => void
    autoFocus?: boolean
}

const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>({
    container: {
        position: 'relative',
        width: '100%',
    },
    input: {
        height: '3.125rem',
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '1.25rem',
        '&:focus': {
            borderColor: colors.green500,
            outlineColor: colors.green500,
        },
        '&:active': {
            borderColor: colors.green500,
            outlineColor: colors.green500,
        },
        '&:focus ~ $label': {
            display: 'none',
        },
        '&:focus ~ $searchButton': {
            borderTopColor: colors.green500,
            borderBottomColor: colors.green500,
            borderRightColor: colors.green500,
        },
    },
    label: {
        display: ({ query }) => (query.length === 0 ? 'inline' : 'none'),
        position: 'absolute',
        pointerEvents: 'none',
        left: '1.25rem',
        fontSize: ({ query }) => (query.length === 0 ? '1.125rem' : '0.75rem'),
        top: ({ query }) => (query.length === 0 ? '50%' : '1.825rem'),
        transform: ({ query }) =>
            query.length === 0 ? 'translateY(-50%)' : 'unset',
        color: colors.labelColor,
        transition: '0.2s ease all',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
            width: 'auto',
            padding: '3px',
            margin: '0',
            borderRadius: '50%',
            background: colors.gray500,
            position: 'absolute',
            right: '68px',
            top: '13px',
            zIndex: '3',
        },
    },
    searchButton: {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
        borderLeft: `1px solid ${colors.borderColor}`,
        borderRight: 'none',
        borderBottom: 'none',
        borderTop: 'none',
        color: colors.white,
        position: 'absolute',
        width: '3.75em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '44px',
        top: '2px',
        right: '2px',
        '&:hover': {
            cursor: 'pointer',
            borderColor: colors.green500,
        },
        '&:active': {
            borderColor: colors.green500,
            outlineColor: colors.green500,
        },
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',

        // Primary specifics
        backgroundColor: ({ typeSearch }) =>
            typeSearch === 'primary' ? colors.white : colors.green500,

        // Secondary specifics
        minWidth: ({ typeSearch }) =>
            typeSearch === 'secondary' ? '8rem' : 'unset',
    },
    icon: {
        // Primary specifics
        display: ({ typeSearch }) =>
            typeSearch === 'primary' ? 'block' : 'none',
        width: '1.325em',
        color: colors.green500,
    },
})

const SearchField: React.FC<Props> = ({
    label,
    required,
    typeSearch = 'primary',
    searchButtonLabel,
    autoFocus,
    onSearch,
    onChange,
    ...props
}) => {
    const [query, setQuery] = useState<string>('')
    const theme = useTheme<Jss.Theme>()
    const classes = useStyles({ ...{ query, typeSearch }, theme })
    const handleSearch = (): void => {
        if (onSearch) {
            onSearch(query)
        }
    }
    return (
        <div className={classes.container} {...props}>
            <Input
                type="search"
                id={label}
                value={query}
                required={required}
                label={label}
                autoFocus={autoFocus}
                autoComplete="off"
                onChange={(e) => {
                    setQuery(e.target.value)

                    if (onChange) {
                        onChange(e.target.value)
                    }
                }}
            />
            {query && (
                <button
                    type="button"
                    className={classes.button}
                    onClick={() => {
                        setQuery('')

                        if (onChange) {
                            onChange('')
                        }
                    }}
                >
                    <Close width="14px" color={colors.green500} />
                </button>
            )}
            <button
                className={classes.searchButton}
                onClick={handleSearch}
                type="submit"
            >
                {typeSearch === 'primary' ? (
                    <SearchIcon className={classes.icon} />
                ) : (
                    <div>{searchButtonLabel}</div>
                )}
            </button>
        </div>
    )
}

SearchField.defaultProps = {
    onSearch: undefined,
    onChange: undefined,
    style: undefined,
    autoFocus: false,
}

export default SearchField
